import { type ClassValue, clsx } from 'clsx';
import { VarianceStatus } from '@/types';

// Re-export from shared package
export {
  // Variance functions
  calculateVariance,
  calculateDailySalesVariance,
  calculateOvernightVariance,
  getVarianceStatus,
  isAccumulatingProduct,
  VARIANCE_THRESHOLDS,
  // Auth utilities
  hasRole,
  isWhitelisted,
  getWhitelistEntry,
  ALLOWED_USERS,
  // Date utilities
  getSouthAfricanDate,
  isTodaySA,
  toSouthAfricanDateString,
} from '@leafstock/shared';

// Utility for className merging
export function cn(...inputs: ClassValue[]) {
  return clsx(inputs);
}

// Format currency
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
  }).format(amount);
}

// Format date
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(d);
}

// Format date with time
export function formatDateTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

// Get variance color
export function getVarianceColor(status: VarianceStatus): string {
  switch (status) {
    case VarianceStatus.ACCEPTABLE:
      return 'text-green-600 bg-green-50';
    case VarianceStatus.WARNING:
      return 'text-yellow-600 bg-yellow-50';
    case VarianceStatus.CRITICAL:
      return 'text-red-600 bg-red-50';
    default:
      return 'text-gray-600 bg-gray-50';
  }
}

// Debounce function
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

// Get stock level status
export function getStockLevel(
  currentStock: number,
  minStock: number,
  maxStock: number
): 'critical' | 'low' | 'good' | 'high' {
  if (currentStock <= minStock * 0.5) return 'critical';
  if (currentStock <= minStock) return 'low';
  if (currentStock >= maxStock) return 'high';
  return 'good';
}

// Get stock percentage
export function getStockPercentage(
  currentStock: number,
  minStock: number,
  maxStock: number
): number {
  return ((currentStock - minStock) / (maxStock - minStock)) * 100;
}

